import React, { useEffect, useState } from 'react';
import CustomModal from '../../../components/CustomModal/CustomModal';
import { Button } from '../../../components/ui/button';
import CustomSelect from '../../../components/CustomSelect/CustomSelect';
import Issue, { IssueStatus } from '../../../types/Issue';
import IssueService from '../../../services/IssueService/IssueService';

interface IssueDetailModalProps {
  open: boolean;
  issue: Issue | null;
  onClose: () => void;
  onUpdated: () => void;
  openSuccessSnackbar: (message: string) => void;
  openErrorSnackbar: (message: string) => void;
}

const GENERIC_ERROR = 'Something went wrong. Please try again later.';

const formatDateTime = (value: string | null): string => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const STATUS_OPTIONS = [
  { value: 'OPEN', label: 'Open' },
  { value: 'RESOLVED', label: 'Resolved' }
];

function IssueDetailModal({
  open,
  issue,
  onClose,
  onUpdated,
  openSuccessSnackbar,
  openErrorSnackbar
}: Readonly<IssueDetailModalProps>): React.ReactElement | null {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState<IssueStatus>('OPEN');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (issue) {
      setTitle(issue.title ?? '');
      setDescription(issue.description ?? '');
      setNotes(issue.notes ?? '');
      setStatus(issue.status);
      setConfirmingDelete(false);
    }
  }, [issue]);

  if (!issue) {
    return null;
  }

  const runAction = async (
    action: () => Promise<void>,
    successMessage: string
  ): Promise<void> => {
    setIsSaving(true);
    try {
      await action();
      openSuccessSnackbar(successMessage);
      onUpdated();
      onClose();
    } catch (err: any) {
      if (err.name !== 'CanceledError') {
        openErrorSnackbar(err.apiError?.message || GENERIC_ERROR);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleSave = (): Promise<void> =>
    runAction(
      () =>
        IssueService.updateIssue(
          issue.id,
          title,
          description,
          notes,
          status
        ).then(() => {}),
      'Issue updated'
    );

  const handleResolve = (): Promise<void> =>
    runAction(
      () => IssueService.resolveIssue(issue.id).then(() => {}),
      'Issue resolved'
    );

  const handleDelete = (): Promise<void> =>
    runAction(() => IssueService.deleteIssue(issue.id), 'Issue deleted');

  return (
    <CustomModal open={open} onClose={onClose} title={issue.reference}>
      <div className='flex flex-col gap-4'>
        <div className='flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground'>
          <span>
            Scope: {issue.scope === 'GLOBAL' ? 'Global' : 'This chatbot'}
          </span>
          <span>Status: {issue.status === 'OPEN' ? 'Open' : 'Resolved'}</span>
          <span>Created: {formatDateTime(issue.createdAt)}</span>
          <span>Last tagged: {formatDateTime(issue.lastTaggedAt)}</span>
          <span>Resolved: {formatDateTime(issue.resolvedAt)}</span>
        </div>

        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium'>Title</span>
          <input
            type='text'
            maxLength={200}
            aria-label='Issue title'
            className='w-full rounded-md border border-input bg-background p-2 text-sm'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder='Short label for quick scanning'
          />
        </div>

        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium'>Description</span>
          <textarea
            id='issue-description'
            aria-label='Issue description'
            className='min-h-[100px] w-full rounded-md border border-input bg-background p-2 text-sm'
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder='Describe the issue'
          />
        </div>

        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium'>Notes</span>
          <textarea
            id='issue-notes'
            aria-label='Issue notes'
            className='min-h-[80px] w-full rounded-md border border-input bg-background p-2 text-sm'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='How it was resolved, why it was deferred, recurrences...'
          />
        </div>

        <div className='flex flex-col gap-1'>
          <span className='text-sm font-medium'>Status</span>
          <CustomSelect
            value={status}
            onValueChange={(value) => setStatus(value as IssueStatus)}
            options={STATUS_OPTIONS}
            className='h-9 text-sm'
          />
        </div>

        {confirmingDelete ? (
          <div className='mt-2 flex items-center justify-between gap-3'>
            <span className='text-muted-foreground text-sm'>
              Delete this issue? This can&apos;t be undone.
            </span>
            <div className='flex shrink-0 gap-2'>
              <Button
                variant='ghost'
                onClick={() => setConfirmingDelete(false)}
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                variant='destructive'
                onClick={handleDelete}
                disabled={isSaving}
              >
                Confirm delete
              </Button>
            </div>
          </div>
        ) : (
          <div className='mt-2 flex items-center justify-between'>
            <Button
              variant='destructive'
              onClick={() => setConfirmingDelete(true)}
              disabled={isSaving}
            >
              Delete
            </Button>
            <div className='flex gap-2'>
              {issue.status === 'OPEN' && (
                <Button
                  variant='outline'
                  onClick={handleResolve}
                  disabled={isSaving}
                >
                  Resolve
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={isSaving || title.trim() === ''}
              >
                Save
              </Button>
            </div>
          </div>
        )}
      </div>
    </CustomModal>
  );
}

export default IssueDetailModal;
